<template>
    <div class="checkout__step-1" :class="{ 'direction-rtl': isRTL }" data-test="checkout-step-1">
        <span @click="closeStepOne" class="checkout__close" data-test="checkout-step-1-close">&times;</span>
        <p class="checkout__progress-title">{{ $t('checkout.progressTitle') }}</p>
        <div class="checkout__progress-bar"></div>

        <h1 v-html="productTitle" class="checkout__title" :class="titleAdditionalClass()"></h1>

        <div class="checkout__input-block" :class="{ 'checkout__input-block--rtl': isRTL }">
            <input
                data-test="checkout-email"
                ref="email"
                id="checkout__input--email"
                type="email"
                autocomplete="email"
                :style="field.style"
                v-model.trim="email"
                @focus="onFocus"
                @blur="onBlur"
                @keyup="onKeyUp"
                :disabled="checkingEmail" />
            <label for="checkout__input--email" :style="label.style" :class="label.classObj">{{ label.text }}</label>
        </div>

        <div
            data-test="submit-email"
            @click="buttonClicked"
            @mousedown="preventDefault"
            class="checkout__button"
            id="checkout__button--submit-step-1"
            v-html="buttonProperties.text"
            :style="buttonProperties.style"></div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import msg from '../../messages';
import i18n from '../../i18n';

const EMAIL_LABEL = i18n.global.t('checkout.pleaseEnterEmail');
const EMAIL_ERROR = i18n.global.t('checkout.enterValidEmail');
const BUTTON_TITLE = i18n.global.t('checkout.getInstantAccess');
const BUTTON_BUSY_TITLE = i18n.global.t('checkout.checkingEmailAddress');

export default {
    data: function () {
        return {
            emailFieldIsFocused: false,
            emailInputAutofocus: true,
            checkingEmail: false,
            label: {
                text: EMAIL_LABEL,
                style: {},
                classObj: {
                    focus: false,
                },
            },
            field: {
                style: {},
            },
            button: {
                default: {
                    text: BUTTON_TITLE,
                    style: { opacity: '100%' },
                },
                disabled: {
                    text: BUTTON_BUSY_TITLE,
                    style: { opacity: '30%' },
                },
            },
        };
    },

    mounted() {
        const is_ios_device = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const is_ios_14 = this.compareVersion(this.iOSversion, '14.0.0') > -1;

        if (screen.width <= 374) {
            this.emailInputAutofocus = false;
        }

        if (this.isMobile && !is_ios_device) {
            this.emailInputAutofocus = false;
        } else if (is_ios_device || navigator.maxTouchPoints > 1) {
            if (window.orientation !== 0) {
                this.emailInputAutofocus = false;
            }
            window.addEventListener(
                'orientationchange',
                function () {
                    if (window.orientation !== 0) {
                        this.emailInputAutofocus = false;
                    } else if (screen.width >= 360 && !this.contact && !is_ios_14) {
                        this.emailInputAutofocus = true;
                    }
                }.bind(this),
            );
        }

        if (this.contact && this.isMobile) {
            this.emailInputAutofocus = false;
        }

        // iOS 14 does not allow input autofocus and ends up with weird UX.
        if (is_ios_14) {
            this.emailInputAutofocus = false;
        }

        if (this.$store.state.contact && this.$store.state.contact.email) {
            this.email = this.$store.state.contact.email;
        }
    },

    computed: {
        email: {
            get() {
                return this.$store.state.emailInput;
            },
            set(value) {
                this.$store.commit('setEmailInput', value);
            },
        },
        ...mapGetters(['product']),
        ...mapState(['contact', 'popup', 'fb_user_info']),
        stepOneOpen() {
            return this.popup.isOpen && this.popup.step === 1;
        },
        productTitle() {
            let line_break = '';
            const proformSpecificSlugs = [
                'iphone-landscape-mastery',
                'capture-it-all',
                'iphone-food-photography',
                'urban-iphone-photography',
                'iphone-editing-academy',
                'iphone-video-academy',
                'signature-preset-collection',
                'drone-mastery',
                'all-access-pass',
            ];
            let proform = proformSpecificSlugs.includes(this.product.slug)
                ? this.$t('checkout.we')
                : this.$t('checkout.i');

            if (this.product.title.length < 28) {
                line_break = '<br/>';
            }

            if (this.product.slug === 'iphone-food-photography') {
                return (
                    `${this.$t('checkout.whereShould')} ` +
                    proform +
                    ` ${this.$t('checkout.send')} ` +
                    line_break +
                    'The Food Course' +
                    (this.isRTL ? '؟' : '?')
                );
            }

            if (this.isTranslated) {
                if (this.currentLanguage === 'es') {
                    return `${this.$t('checkout.whereShould')} ${proform} ${this.$t('checkout.send')} ${line_break} El ${this.product.title}?`;
                }
                if (this.currentLanguage === 'de') {
                    return `${this.$t('checkout.whereShould')} ${proform} ${line_break} ${this.product.title} ${this.$t('checkout.send')}?`;
                }
                if (this.currentLanguage === 'fr') {
                    return `${this.$t('checkout.whereShould')} ${proform} ${this.$t('checkout.send')} ${this.product.slug !== 'video-editing-academy' && this.product.slug !== 'capture-it-all' ? "l'" : ''}${this.product.title}?`;
                }
            }

            return (
                `${this.$t('checkout.whereShould')} ` +
                proform +
                ` ${this.$t('checkout.send')} ` +
                line_break +
                this.product.title +
                (this.isRTL ? '؟' : '?')
            );
        },
        buttonProperties() {
            return this.checkingEmail ? this.button.disabled : this.button.default;
        },
        iOSversion() {
            if (/iP(hone|od|ad)/.test(window.navigator.platform)) {
                const v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
                return parseInt(v[1], 10) + '.' + parseInt(v[2], 10) + '.' + parseInt(v[3] || 0, 10);
            }
        },
        currentLanguage() {
            // Use the reactive language from the plugin if available, fallback to window.App
            return this.$currentLanguage?.value || window.App.language;
        },
        isTranslated() {
            return this.currentLanguage !== 'en';
        },
        isTranslatedTitle() {
            const url = new URL(window.location.href);

            return url.searchParams.get('lang-variant') === '1';
        },
        isRTL() {
            return window.App.isRTL;
        },
    },

    watch: {
        // This is triggered every time stepOneOpen is changed. E.g. already bought sends customer back to email input.
        stepOneOpen: function () {
            if (this.stepOneOpen && this.emailInputAutofocus) {
                // To fix bug in mobile Safari starting version 12.2.0 where popups shows behind the keyboard
                // compareVersion() returns: 0 if same, 1 if higher, -1 if lower version incorect format
                // We don't do that if already-bough popup is open because it brakes the design
                // Don't emit this function when we have to show faceobook
                if (this.compareVersion(this.iOSversion, '12.2.0') > -1 && !this.$store.state.popup.errorCode) {
                    this.eventBus.emit('SET_FIRST_AUTOFOCUS');
                }

                // When user comes back from step2 email field is not yet rendered
                // $nextThick executes when Vue is done propagating all changes
                this.$nextTick(() => {
                    this.$refs.email.focus();
                });
            } else {
                this.$refs.email.blur();
            }

            if (this.stepOneOpen && this.email !== '') {
                this.label.classObj.focus = true;
            }
        },
    },

    methods: {
        titleAdditionalClass() {
            let cssClass = '';
            if (this.productTitle.length > 56) {
                cssClass += 'checkout__title--longer';
            } else if (this.productTitle.length > 45) {
                cssClass += 'checkout__title--long';
            }
            return cssClass;
        },
        onFocus() {
            this.emailFieldIsFocused = true;
            this.label.classObj.focus = true;
            this.label.text = EMAIL_LABEL;
            this.makeFieldNeutral();
            if (this.emailInputAutofocus && /iPhone/i.test(window.navigator.userAgent)) {
                this.eventBus.emit('OPEN_KEYBOARD');
            }
        },
        onBlur() {
            this.emailFieldIsFocused = false;
            if (this.email === '') {
                this.label.classObj.focus = false;
                this.label.text = EMAIL_LABEL;
            }
            this.eventBus.emit('CLOSE_KEYBOARD');
        },
        onKeyUp(event) {
            if (event.keyCode == 13) {
                // Enter button hit
                this.buttonClicked();
            } else {
                this.field.style = {
                    color: '#333',
                    border: '1px solid #c5c5c5',
                    outline: '0',
                };
                this.label.style = {
                    color: '#c5c5c5',
                };
                this.label.text = EMAIL_LABEL;
                this.label.classObj.focus = true;
            }
        },
        async buttonClicked(event) {
            if (this.checkingEmail) {
                return;
            }

            window.ipse.dispatch({
                action: 'submit_email_clicked',
                data_event: {
                    email: this.email,
                },
            });

            logger.addCheckoutBreadcrumb({
                message: msg.checkout.stepOneSubmitClicked,
                data: { email: this.email },
            });

            this.makeFieldNeutral();

            if (!this.email || !this.isEmail(this.email)) {
                logger.addCheckoutBreadcrumb({
                    message: msg.checkout.stepOneLocalValidationFailed,
                    data: { email: this.email },
                });

                this.makeFieldInvalid();

                return;
            }

            /**
             * We have to prepare ApplePaySession before any async calls.
             * Otherwise "Must create a new ApplePaySession from a user gesture handler." will occur.
             */
            this.eventBus.emit('CREATE_APPLE_PAY_SESSION');

            let response;

            try {
                this.checkingEmail = true;
                response = await this.$store.dispatch('postCheckoutStarted');
            } catch {
                // pass
            } finally {
                this.checkingEmail = false;
            }

            if (!response || response.errors?.email !== undefined) {
                this.makeFieldInvalid();

                return;
            }

            if (response.already_purchased) {
                logger.addCheckoutBreadcrumb({
                    message: msg.checkout.stepOneAlreadyPurchased,
                    data: { email: this.email },
                });

                this.$store.commit('openCheckout', {
                    step: 1,
                    errorCode: 'already-purchased',
                });

                return;
            }

            this.$store.commit('openCheckout', {
                step: 2,
            });

            logger.addCheckoutBreadcrumb({
                message: msg.checkout.stepOneComplete,
                data: { email: this.email },
            });

            this.tracker.fireCheckoutStartedEvent();
        },
        isEmail(email) {
            var re =
                /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
            return re.test(String(email).toLowerCase());
        },
        makeFieldInvalid() {
            this.$store.commit('makeEmailFieldInvalid', true);

            this.$refs.email.focus();

            this.field.style = {
                color: '#d80c0c',
                border: '1px solid #d80c0c',
                outline: '0',
            };

            this.label.style = {
                color: '#d80c0c',
            };
            this.label.text = EMAIL_ERROR;
            this.label.classObj.focus = true;
        },
        makeFieldNeutral() {
            this.$store.commit('makeEmailFieldInvalid', false);

            this.label.style = {};
            this.field.style = {};
        },
        preventDefault(event) {
            // @HACK for blur and click event overlapping for safari mobile devices
            event.preventDefault();
        },
        compareVersion(v1, v2) {
            if (typeof v1 !== 'string') return -1;
            if (typeof v2 !== 'string') return -1;
            v1 = v1.split('.');
            v2 = v2.split('.');
            const k = Math.min(v1.length, v2.length);
            for (let i = 0; i < k; ++i) {
                v1[i] = parseInt(v1[i], 10);
                v2[i] = parseInt(v2[i], 10);
                if (v1[i] > v2[i]) return 1;
                if (v1[i] < v2[i]) return -1;
            }
            return v1.length == v2.length ? 0 : v1.length < v2.length ? -1 : 1;
        },
        closeStepOne() {
            logger.addCheckoutBreadcrumb({ message: msg.checkout.stepOneCloseButtonClicked });

            this.$store.commit('closeCheckout');
        },
    },
};
</script>
